/**
 * Shared helpers to build map marker GeoJSON features from normalized stop lists.
 */
const isValidNumber = value => typeof value === 'number' && Number.isFinite(value)

const toFiniteNumber = value => {
  if (value === null || value === undefined || value === '') return null
  const num = parseFloat(value)
  return Number.isFinite(num) ? num : null
}

/**
 * Builds a GeoJSON Feature for a single stop. Returns null if the stop has no valid coordinates.
 *
 * @param {Object} stop - normalized stop (must have latitude/longitude or lat/lng)
 * @param {Object} [extraProperties] - extra fields merged into feature.properties
 * @returns {Object|null} GeoJSON Feature or null
 */
export const buildStopFeature = (stop, extraProperties = {}) => {
  if (!stop) return null
  const lat = toFiniteNumber(stop.latitude ?? stop.lat)
  const lng = toFiniteNumber(stop.longitude ?? stop.lng)
  if (lat === null || lng === null) return null

  const id = stop.workOrderId || stop.id || stop.configuredServiceEventId
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: {
      ...stop,
      id,
      latitude: lat,
      longitude: lng,
      ...extraProperties,
    },
  }
}

/**
 * Builds the sequence line GeoJSON LineString from an ordered list of stops.
 * Skips stops with missing/invalid coordinates.
 *
 * @param {Object[]} stops - ordered list of normalized stops
 * @returns {Object} GeoJSON Feature with LineString geometry (empty coords if none valid)
 */
export const buildSequenceLine = stops => {
  if (!Array.isArray(stops)) {
    return { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } }
  }
  const coordinates = []
  stops.forEach(stop => {
    const lat = toFiniteNumber(stop?.latitude ?? stop?.lat)
    const lng = toFiniteNumber(stop?.longitude ?? stop?.lng)
    if (lat !== null && lng !== null) coordinates.push([lng, lat])
  })
  return { type: 'Feature', geometry: { type: 'LineString', coordinates } }
}

/**
 * Builds the map "route" object (markers + sequenceLine + color) consumable by
 * CommonSelectStopsToMoveMap from a normalized stop list and route metadata.
 *
 * @param {Object} params
 * @param {string} params.routeId
 * @param {string} [params.color]
 * @param {Object[]} params.stops - normalized route stops, ordered by display position
 * @param {Object} [params.toBeAssignedStop] - optional pending stop to render as a "to be assigned" marker
 * @returns {Object} { id, markers, sequenceLine, color }
 */
export const buildMapRoute = ({ routeId, color, stops = [], toBeAssignedStop }) => {
  const routeStopFeatures = stops
    .map((stop, index) =>
      buildStopFeature(stop, {
        position: index + 1,
        originalPosition: index + 1,
        color,
        routeId,
      })
    )
    .filter(Boolean)

  const toBeAssignedFeature = toBeAssignedStop
    ? buildStopFeature(toBeAssignedStop, {
        icon: 'to-be-assigned-marker',
        position: '',
      })
    : null

  const markers = toBeAssignedFeature ? [...routeStopFeatures, toBeAssignedFeature] : routeStopFeatures

  return {
    id: routeId,
    markers,
    sequenceLine: buildSequenceLine(stops),
    color,
  }
}

export { isValidNumber, toFiniteNumber }
